import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateTourEnv } from '@/domain/impact-calculate-tour/entities/calculate-tour-env.entity';
import { selectImpactCalculateTourEnvForProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfTourPage4({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const tour = useAppSelector(
    selectCurrentProjectTour,
  ) satisfies ProjectTour | null;
  const tourId = tour?.id ?? 0;

  const impactEnv = useAppSelector(
    selectImpactCalculateTourEnvForProject(tourId),
  ) satisfies CalculateTourEnv;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!tour) {
    return <h1>{translate('PdfExports.tour-not-found')}</h1>;
  }

  const general = impactEnv.general;
  const topics = general.topics;
  const impactEnvScore100 =
    Math.round((general.score * 100) / general.maxScore) || 0;

  return (
    <div id="page4" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{tour.name}</h1>
            {tour.startDate && tour.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="tour-date">
                  {formatProjectDatesTz(tour.startDate, tour.endDate)}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.env-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEnvScore100}</span>/ 100
            </h4>
            <h5>
              {general.score} / {displayDecimal(general.maxScore)}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="env"
              scoreLetter={general.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.EQUIPMENT_AND_SCENOGRAPHY')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.EQUIPMENT_AND_SCENOGRAPHY.score)}
                  </span>{' '}
                  / {displayDecimal(topics.EQUIPMENT_AND_SCENOGRAPHY.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      topics.EQUIPMENT_AND_SCENOGRAPHY.sections,
                    ) as [
                      keyof typeof topics.EQUIPMENT_AND_SCENOGRAPHY.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.EQUIPMENT_AND_SCENOGRAPHY.score}
                  maxScore={topics.EQUIPMENT_AND_SCENOGRAPHY.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.FOOD_BEVERAGES')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.FOOD_BEVERAGES.score)}
                  </span>{' '}
                  / {displayDecimal(topics.FOOD_BEVERAGES.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(topics.FOOD_BEVERAGES.sections) as [
                      keyof typeof topics.FOOD_BEVERAGES.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.FOOD_BEVERAGES.score}
                  maxScore={topics.FOOD_BEVERAGES.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.RESOURCES_POLLUTION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.RESOURCES_POLLUTION.score)}
                  </span>{' '}
                  / {displayDecimal(topics.RESOURCES_POLLUTION.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(topics.RESOURCES_POLLUTION.sections) as [
                      keyof typeof topics.RESOURCES_POLLUTION.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.RESOURCES_POLLUTION.score}
                  maxScore={topics.RESOURCES_POLLUTION.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.COMMUNICATION')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.COMMUNICATION.score)}
                  </span>{' '}
                  / {displayDecimal(topics.COMMUNICATION.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(topics.COMMUNICATION.sections) as [
                      keyof typeof topics.COMMUNICATION.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.COMMUNICATION.score}
                  maxScore={topics.COMMUNICATION.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.PUBLIC_ENGAGEMENT.score)}
                  </span>{' '}
                  / {displayDecimal(topics.PUBLIC_ENGAGEMENT.maxScore)}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(topics.PUBLIC_ENGAGEMENT.sections) as [
                      keyof typeof topics.PUBLIC_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Env.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {displayDecimal(maxScore)}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.PUBLIC_ENGAGEMENT.score}
                  maxScore={topics.PUBLIC_ENGAGEMENT.maxScore}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              width: 340,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-back-to-co2-ontour')}
          </a>
          <a href="#page5" className="tag-pill">
            {translate('PdfExports.go-to-ess')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={4} total={totalPages}></PdfProjectPager>
    </div>
  );
}
